import React, { useRef, useState, ReactNode } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  arrow,
  FloatingArrow,
  Placement,
} from "@floating-ui/react";
import { P } from "common/components/Paragraph";

export interface CommonTooltipProps {
  id?: string;
  description: ReactNode;
  variant?: "leftArrow" | "rightArrow" | "topArrow" | "bottomArrow" | "none";
  children: ReactNode;
  hide?: boolean;
}

export interface SmallTooltipProps extends CommonTooltipProps {
  size: "sm";
}
export interface MediumTooltipProps extends CommonTooltipProps {
  size: "md";
  title?: string;
}

export type TooltipProps = SmallTooltipProps | MediumTooltipProps;

export const Tooltip: React.FC<TooltipProps> = (props) => {
  // Constants
  const { id, description, variant = "none", size, children, hide } = props;
  const title = size === "md" && (props as MediumTooltipProps).title;
  const placements: { [key in NonNullable<TooltipProps["variant"]>]: Placement } = {
    leftArrow: "left",
    rightArrow: "right",
    topArrow: "top",
    bottomArrow: "bottom",
    none: "top",
  };
  const ownDarkTheme = "dark:bg-secondary-50 dark:text-shades-Black";
  const ownClassName = `bg-shades-Black text-shades-White rounded-3xl p-sm shadow-cards/shadow ${ownDarkTheme}`;
  const arrowDarkTheme = "dark:fill-secondary-50 dark:[&>path:first-of-type]:stroke-secondary-100";
  const arrowClassName = `fill-shades-Black ${arrowDarkTheme}`;
  const ownId = id ? { id } : {};

  // Local state
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const isArrow = variant.includes("Arrow");
  const tooltipOffset = isArrow ? 10 : 5;
  const middleware = [
    offset(tooltipOffset),
    shift(),
    flip({
      fallbackAxisSideDirection: "start",
    }),
    arrow({
      element: arrowRef,
    }),
  ];

  // Hooks
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: placements[variant],
    whileElementsMounted: autoUpdate,
    middleware,
  });
  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (hide) return children;

  return (
    <>
      <div className="w-fit max-w-full overflow-hidden" ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </div>
      <FloatingPortal>
        {isOpen && (
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 9999 }} {...getFloatingProps()}>
            <div {...ownId} className={ownClassName}>
              {isArrow && <FloatingArrow className={arrowClassName} strokeWidth={1} ref={arrowRef} context={context} />}
              {title && (
                <P weight="medium" size="sm">
                  {title}
                </P>
              )}
              <div className={`font-normal text-sm leading-4 ${!title ? "text-center" : ""}`}>{description}</div>
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};
